<template>
  <div class="home-page">

    <!-- HERO -->
    <section class="hero">
      <div class="container">
        <div class="hero__eyebrow">
          <span class="hero__badge"><i class="fa fa-magic"></i> {{ hero_badge }}</span>
          <span class="hero__badge-sub">{{ hero_badge_sub }}</span>
        </div>
        <div class="hero__grid">
          <div class="hero__left">
            <h1 class="hero__title">{{ hero_title_prefix }} <span class="hero__accent">{{ hero_title_accent }}</span> {{ hero_title_suffix }}</h1>
            <p class="hero__sub">{{ hero_subtitle }}</p>
            <form class="hero__search" @submit.prevent="doSearch">
              <div class="hero__search-wrap">
                <i class="fa fa-search hero__search-icon"></i>
                <input v-model="query" type="text" class="hero__search-input" :placeholder="hero_search_placeholder" />
                <button type="submit" class="hero__search-btn"><i class="fa fa-search"></i> SherlockIT</button>
              </div>
              <div class="hero__samples">
                <div class="hero__samples-label">Sample cases:</div>
                <div class="hero__samples-chips">
                  <button v-for="s in samples" :key="s" type="button" class="hero__sample-chip" @click="query=s;doSearch()">{{ s }} <i class="fa fa-arrow-right hero__chip-arrow"></i></button>
                </div>
              </div>
            </form>
            <div class="hero__trust">
              <span class="hero__trust-item"><i class="fa fa-globe"></i> 120+ jurisdictions</span>
              <span class="hero__trust-item"><i class="fa fa-shield"></i> Court-admissible chain of custody</span>
              <span class="hero__trust-item"><i class="fa fa-refresh"></i> Real-time, not stale</span>
              <span class="hero__trust-item"><i class="fa fa-magic"></i> Explainable AI</span>
            </div>
          </div>
          <div class="hero__card">
            <div class="hero__card-grid-bg"></div>
            <div class="hero__card-inner">
              <div class="hero__card-header">
                <span class="hero__card-tag"><i class="fa fa-sitemap"></i> {{ hero_card_tag }}</span>
                <span class="hero__card-live"><span class="hero__card-dot"></span> Live Data</span>
              </div>
              <div class="hero__card-query">{{ hero_card_query }}</div>
              <div class="hero__card-answer">{{ hero_card_answer }} <span class="hero__card-verdict">{{ hero_card_verdict }}</span></div>
              <div class="hero__card-steps-label">How we got there</div>
              <div class="hero__card-steps">
                <div v-for="step in hero_card_steps" :key="step" class="hero__card-step"><i class="fa fa-check-circle"></i><span>{{ step }}</span></div>
              </div>
              <div class="hero__card-footer">
                <span>{{ hero_card_footer_left }}</span>
                <span>{{ hero_card_footer_right }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- THE EVIDENCE GAP -->
    <section class="evidence">
      <div class="container">
        <div class="evidence__header">
          <div class="eyebrow">{{ evidence_eyebrow }}</div>
          <h2 class="section-title">{{ evidence_title }}</h2>
          <p class="section-sub">{{ evidence_sub }}</p>
        </div>
        <div class="evidence__card">
          <div class="evidence__card-inner">
            <div class="evidence__regs">
              <div class="evidence__regs-label">{{ evidence_regs_label }}</div>
              <div class="evidence__regs-tags">
                <span v-for="r in regs" :key="r" class="evidence__reg-tag">{{ r }}</span>
              </div>
            </div>
            <div class="evidence__stakes">
              <div class="evidence__stakes-label">{{ evidence_stakes_label }}</div>
              <div class="evidence__stakes-num">{{ evidence_stakes_num }}</div>
              <div class="evidence__stakes-sub">{{ evidence_stakes_sub }}</div>
            </div>
          </div>
        </div>
        <div class="evidence__cta">
          <a href="#" @click.prevent="doSearch" class="btn-primary">{{ evidence_cta }} <i class="fa fa-arrow-right"></i></a>
        </div>
      </div>
    </section>

    <!-- WHO IT'S FOR -->
    <section class="teams">
      <div class="container">
        <div class="teams__header">
          <div class="eyebrow">{{ teams_eyebrow }}</div>
          <h2 class="section-title">{{ teams_title }}</h2>
          <p class="section-sub">{{ teams_sub }}</p>
        </div>
        <div class="teams__grid">
          <div v-for="t in teams" :key="t.name" class="team-card">
            <div class="team-card__icon"><i :class="t.icon"></i></div>
            <div class="team-card__name">{{ t.name }}</div>
            <div class="team-card__desc">{{ t.who }}</div>
            <div class="team-card__tags">
              <span v-for="tag in t.tags" :key="tag" class="team-tag">{{ tag }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- PROOF / USE CASES -->
    <section class="proof">
      <div class="container">
        <div class="proof__header">
          <div class="eyebrow">{{ proof_eyebrow }}</div>
          <h2 class="section-title">{{ proof_title }}</h2>
        </div>
        <div class="proof__grid">
          <div v-for="c in cases" :key="c.title" class="proof-card">
            <div class="proof-card__tag">{{ c.tag }}</div>
            <div class="proof-card__title">{{ c.title }}</div>
            <div class="proof-card__body">
              <div class="proof-card__block">
                <div class="proof-card__block-label before">Before</div>
                <div class="proof-card__block-text">{{ c.before }}</div>
              </div>
              <div class="proof-card__block">
                <div class="proof-card__block-label after">With SherlockIT</div>
                <div class="proof-card__block-text">{{ c.after }}</div>
              </div>
            </div>
            <div class="proof-card__footer">
              <span class="proof-card__metric">{{ c.metric }}</span>
              <span class="proof-card__open">Open case <i class="fa fa-arrow-right"></i></span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- HOW IT WORKS + KNOWLEDGE GRAPH -->
    <section class="how">
      <div class="container">
        <div class="how__grid">
          <div class="how__left">
            <div class="eyebrow">{{ how_eyebrow }}</div>
            <h2 class="section-title">{{ how_title }}</h2>
            <p class="section-sub">{{ how_sub }}</p>
            <div class="how__layers">
              <div v-for="layer in layers" :key="layer.title" class="layer-card">
                <div class="layer-card__header">
                  <div class="layer-card__icon"><i :class="layer.icon"></i></div>
                  <div class="layer-card__title">{{ layer.title }}</div>
                  <span class="layer-card__tag">{{ layer.tag }}</span>
                </div>
                <p class="layer-card__desc">{{ layer.desc }}</p>
                <div class="layer-card__proofs">
                  <span v-for="p in layer.proofs" :key="p" class="layer-proof"><i class="fa fa-check-circle"></i> {{ p }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="how__right">
            <div class="kg-panel">
              <div class="kg-panel__badge"><span class="kg-panel__dot"></span>Live knowledge graph</div>
              <div class="kg-panel__info">8 nodes · 8 edges · cited</div>
              <svg viewBox="0 0 660 440" class="kg-svg">
                <defs>
                  <pattern id="kg-grid" width="30" height="30" patternUnits="userSpaceOnUse">
                    <path d="M 30 0 L 0 0 0 30" fill="none" stroke="#e2e8f0" stroke-width="0.5"></path>
                  </pattern>
                </defs>
                <rect width="660" height="440" fill="url(#kg-grid)" opacity="0.6"></rect>
                <g v-for="(edge, i) in kgEdges" :key="i">
                  <line
                    :x1="kgNodes[edge.from].x" :y1="kgNodes[edge.from].y"
                    :x2="kgNodes[edge.to].x"   :y2="kgNodes[edge.to].y"
                    :stroke="kgPulse === i ? '#575ECF' : '#e2e8f0'"
                    :stroke-width="kgPulse === i ? 2 : 1.2"
                    :stroke-dasharray="kgPulse === i ? '0' : '4 4'"
                    :opacity="kgPulse === i ? 1 : 0.7"
                  ></line>
                  <text v-if="kgPulse === i"
                    :x="(kgNodes[edge.from].x + kgNodes[edge.to].x) / 2"
                    :y="(kgNodes[edge.from].y + kgNodes[edge.to].y) / 2 - 6"
                    text-anchor="middle" fill="#575ECF" font-size="10" font-weight="600">{{ edge.label }}</text>
                </g>
                <g v-for="node in kgNodes" :key="node.id" :transform="`translate(${node.x - node.w / 2}, ${node.y - 14})`">
                  <rect :width="node.w" height="28" rx="14" :fill="node.fill" :stroke="node.stroke" stroke-width="1.5"></rect>
                  <text :x="node.w / 2" y="18" text-anchor="middle" font-size="11" font-weight="600" :fill="node.textColor">{{ node.label }}</text>
                </g>
              </svg>
              <div class="kg-panel__legend">
                <span class="kg-legend-item kg-legend-item--primary">Entity</span>
                <span class="kg-legend-item kg-legend-item--danger">Sanctioned</span>
                <span class="kg-legend-item kg-legend-item--person">Person / UBO</span>
                <span class="kg-legend-item kg-legend-item--neutral">Registry / Doc</span>
              </div>
            </div>
            <div class="kg-caption">{{ kg_caption }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- GLOBAL + SOVEREIGN BAND -->
    <section class="sovereign">
      <div class="sovereign__grid-bg"></div>
      <div class="container">
        <div class="sovereign__inner">
          <div class="sovereign__left">
            <div class="sovereign__eyebrow"><i class="fa fa-globe"></i> {{ sovereign_eyebrow }}</div>
            <h2 class="sovereign__title">{{ sovereign_title }}</h2>
            <p class="sovereign__desc">{{ sovereign_desc }}</p>
            <div class="sovereign__tags">
              <span v-for="t in sovereignTags" :key="t" class="sovereign__tag">{{ t }}</span>
            </div>
          </div>
          <div class="sovereign__cards">
            <div v-for="b in sovereignBlocks" :key="b.title" class="sovereign-card">
              <i :class="b.icon" class="sovereign-card__icon"></i>
              <div class="sovereign-card__title">{{ b.title }}</div>
              <div class="sovereign-card__desc">{{ b.desc }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- WAYS TO DEPLOY -->
    <section class="deploy">
      <div class="container">
        <div class="deploy__grid">
          <div class="deploy__left">
            <div class="eyebrow">{{ deploy_eyebrow }}</div>
            <h2 class="section-title">{{ deploy_title }}</h2>
            <p class="section-sub">{{ deploy_sub }}</p>
          </div>
          <div class="deploy__cards">
            <div v-for="d in deployments" :key="d.name" class="deploy-card">
              <i :class="d.icon" class="deploy-card__icon"></i>
              <div class="deploy-card__name">{{ d.name }}</div>
              <div class="deploy-card__desc">{{ d.desc }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FINAL CTA -->
    <section class="final-cta-wrap">
      <div class="container">
        <div class="final-cta">
          <div class="final-cta__grid-bg"></div>
          <div class="final-cta__inner">
            <div class="final-cta__badge"><i class="fa fa-magic"></i> {{ cta_badge }}</div>
            <h3 class="final-cta__title">{{ cta_title }}</h3>
            <p class="final-cta__sub">{{ cta_sub }}</p>
            <div class="final-cta__actions">
              <router-link to="/search" class="final-cta-btn final-cta-btn--white">{{ cta_btn_primary }} <i class="fa fa-arrow-right"></i></router-link>
              <router-link to="/contact-us" class="final-cta-btn final-cta-btn--ghost">{{ cta_btn_ghost }}</router-link>
            </div>
            <div class="final-cta__quote"><i class="fa fa-quote-left"></i> {{ cta_quote }}</div>
          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
import { cmsHome } from '@/services/cms';

export default {
  metaInfo() {
    return { title: "SherlockIT — The Corporate Intelligence Engine for regulated industries" };
  },
  data() {
    return {
      query: "",
      _kgInterval: null,
      kgPulse: 0,
      // ── CMS text fields (overwritten by mounted) ──────────────────────────
      hero_badge: "For compliance, financial-crime, ESG & cyber-supply-chain teams",
      hero_badge_sub: "Cited · Explainable · Court-admissible",
      hero_title_prefix: "The",
      hero_title_accent: "Corporate Intelligence Engine",
      hero_title_suffix: "for regulated industries.",
      hero_subtitle: "Verified, real-time corporate intelligence from 120+ official registries, built for teams that need audit-grade evidence, not best-guess data.",
      hero_search_placeholder: "Search a company, registry ID, or person…",
      hero_card_tag: "A real answer, in seconds",
      hero_card_query: 'You asked · "Is this supplier safe to onboard?"',
      hero_card_answer: "Müller Precision (DE) traces through a Cyprus holding to a parent on the EU sanctions list.",
      hero_card_verdict: "Do not onboard.",
      hero_card_footer_left: "4 sources cited · Chain of custody signed",
      hero_card_footer_right: "~3.2s",
      evidence_eyebrow: "The evidence gap",
      evidence_title: "Compliance now demands evidence you can't gather by hand.",
      evidence_sub: "Regulators require audit-grade proof on every entity you touch — while that data sits fragmented across 200+ official registries in dozens of languages. The gap between what the law requires and what teams can actually prove is the problem we solve.",
      evidence_regs_label: "Built for the regimes raising the bar",
      evidence_stakes_label: "Stakes",
      evidence_stakes_num: "Up to 2% of global turnover",
      evidence_stakes_sub: "Plus personal liability for directors under new EU due-diligence rules.",
      evidence_cta: "Run a verified search",
      teams_eyebrow: "Built for",
      teams_title: "Teams that can't afford to be wrong about an entity.",
      teams_sub: "One verified-data infrastructure, four high-stakes jobs.",
      proof_eyebrow: "Proof",
      proof_title: "From two weeks of investigation to minutes.",
      how_eyebrow: "How it works",
      how_title: "How it works — and why it holds up in front of a regulator.",
      how_sub: "Three layers. Each layer carries the proof point that makes it defensible.",
      kg_caption: "Live trace: Müller Precision → Cyprus Holding → sanctioned parent. Every node cited to source.",
      sovereign_eyebrow: "Global coverage · Sovereign by deployment",
      sovereign_title: "One platform. 120+ jurisdictions. Deployed where your data must live.",
      sovereign_desc: "SherlockIT runs globally — covering registries across the Americas, Europe, Asia, Africa, and the GCC — and deploys under the data-residency, language, and regulatory regime each customer requires. Whether you're a global bank consolidating KYC, or a national regulator with strict sovereignty mandates, the same platform conforms to your perimeter.",
      deploy_eyebrow: "Ways to deploy",
      deploy_title: "From an API call to a sovereign deployment.",
      deploy_sub: "One verified-data layer, four ways to consume it. Start with a paid PoC on a defined use case — scale to institutional volume.",
      cta_badge: "See it end-to-end",
      cta_title: "Audit-ready intelligence, in minutes.",
      cta_sub: "See exactly how SherlockIT resolves a cross-jurisdiction embezzlement structure end-to-end — with citations.",
      cta_btn_primary: "Open the embezzlement case",
      cta_btn_ghost: "Talk to the team",
      cta_quote: "Engaging with financial institutions, governments, and cybersecurity platforms worldwide.",
      // ── List content ──────────────────────────────────────────────────────
      hero_card_steps: [
        "Resolved entity across 3 jurisdictions (DE → CY → RU)",
        "Pulled latest filing from Handelsregister (Germany's official company register)",
        "Cross-checked OFAC + EU consolidated sanctions",
        "Generated audit-ready case file with citations",
      ],
      trustItems: ["120+ jurisdictions", "Court-admissible chain of custody", "Real-time, not stale", "Explainable AI"],
      samples: ["Müller Precision", "Coral Azul", "Falcon Trade", "HRB 184729"],
      regs: ["CSDDD", "LkSG", "NIS2", "DORA", "AMLA"],
      teams: [
        { icon: "fa fa-shield", name: "Compliance & AML", who: "Banks, fintechs, law firms, AMLA-regulated entities.", tags: ["KYB", "Sanctions", "UBO tracing"] },
        { icon: "fa fa-industry", name: "Supply Chain & ESG", who: "Manufacturers, procurement, ESG platforms.", tags: ["Supplier mapping", "Forced-labor exposure", "Greenwashing"] },
        { icon: "fa fa-sitemap", name: "Cybersecurity", who: "CISOs, vendor-risk and threat-intel teams.", tags: ["Vendor verification", "Entity attribution", "Shell-vendor detection"] },
        { icon: "fa fa-university", name: "Government", who: "Regulators, procurement and financial-crime units.", tags: ["Procurement transparency", "Cross-jurisdiction investigations"] },
      ],
      cases: [
        { tag: "AML / Financial crime", title: "The embezzled-funds case", before: "20 shell companies in Madeira. Trail went cold at the jurisdictional jump.", after: "Cross-jurisdiction structure resolved to the Bahamas. Verified data, with full citations.", metric: "2 weeks → minutes" },
        { tag: "Supply chain & ESG", title: "Greenwashing detection", before: "Clean Tier-1 supplier in Germany hides a parent in a high-risk jurisdiction via Cyprus.", after: "Multi-tier ownership graph surfaces the sanctioned parent in seconds.", metric: "3 jurisdictions, 1 query" },
        { tag: "Cybersecurity", title: "Identity attribution", before: "Attack traced to a spoofed vendor with no verified corporate footprint.", after: "IP and domain mapped to a verified legal entity — or flagged as unverifiable.", metric: "IP → legal entity" },
      ],
      layers: [
        {
          icon: "fa fa-database", tag: "Layer 1", title: "Truth · Verified at the source",
          desc: "Direct APIs to 120+ official registries. Multilingual, standardised, timestamped — every field carries a citation to its source filing.",
          proofs: ["Court-admissible chain of custody", "No third-party aggregation", "Real-time, not stale"],
        },
        {
          icon: "fa fa-sitemap", tag: "Layer 2", title: "Intelligence · Explainable by design",
          desc: "Entities, officers, addresses and UBOs woven into a living knowledge graph. Our LLMs reason over that graph — not the open web — and cite the exact node and edge they used.",
          proofs: ["No hallucinations — every claim traceable", "Arabic & multilingual NLP", "Reversible reasoning with confidence signals"],
        },
        {
          icon: "fa fa-bolt", tag: "Layer 3", title: "Action · Agentic, at machine speed",
          desc: "Agentic workflows via MCP drop into your AML, KYB, vendor-risk, GRC and SOC tooling. Continuous monitoring fetches, verifies, scores, and acts — not just on demand.",
          proofs: ["Always-on monitoring, not point-in-time", "One API call replaces a week of analyst work", "Thousands of checks/day at stable latency"],
        },
      ],
      sovereignTags: ["In-region cloud (EU, US, GCC, APAC)", "On-premise & air-gapped", "Arabic & multilingual NLP", "Customer-held keys", "Local-language registries"],
      sovereignBlocks: [
        { icon: "fa fa-globe", title: "Global registry coverage", desc: "120+ official registries across the Americas, EMEA, APAC and the GCC — with full chain of custody on every record." },
        { icon: "fa fa-server", title: "Sovereign deployment", desc: "Choose your region, or run fully on-premise / air-gapped. Your keys, your perimeter, your jurisdiction." },
        { icon: "fa fa-language", title: "Arabic & multilingual", desc: "Normalised entity resolution across Arabic, Latin, Cyrillic and CJK scripts — built for cross-border investigations." },
        { icon: "fa fa-shield", title: "GCC & national-programme ready", desc: "Aligned with Qatar Vision 2030, UAE & KSA digital-trust mandates, and national AML / procurement programmes." },
      ],
      deployments: [
        { icon: "fa fa-plug", name: "Master API", desc: "Risk, graph, and document endpoints for high-volume automation. MCP-native — drops straight into your agents." },
        { icon: "fa fa-search", name: "SaaS Portal", desc: "Analyst console for ad-hoc investigations, certified document retrieval, and case files." },
        { icon: "fa fa-refresh", name: "Continuous monitoring", desc: "Always-on entity surveillance. Risk surfaces before you ask — not point-in-time." },
        { icon: "fa fa-server", name: "On-prem / Sovereign", desc: "Air-gapped or in-country deployment for regulators and national programmes. Your keys, your perimeter." },
      ],
      kgNodes: [
        { id: "mp",   label: "Müller Precision",  x: 300, y: 200, w: 99.2,  fill: "#575ECF", stroke: "#575ECF", textColor: "#fff"    },
        { id: "cy",   label: "Cyprus Holding Ltd", x: 470, y: 130, w: 111.6, fill: "#575ECF", stroke: "#575ECF", textColor: "#fff"    },
        { id: "ru",   label: "Sanctioned Parent",  x: 540, y: 250, w: 105.4, fill: "#dc2626", stroke: "#b91c1c", textColor: "#fff"    },
        { id: "ofac", label: "OFAC SDN",           x: 470, y: 360, w: 70,    fill: "#dc2626", stroke: "#b91c1c", textColor: "#fff"    },
        { id: "ubo",  label: "I. Petrov · UBO",    x: 320, y: 360, w: 93,    fill: "#fff",    stroke: "#575ECF", textColor: "#0a1428" },
        { id: "reg",  label: "Handelsregister",    x: 130, y: 150, w: 93,    fill: "#fff",    stroke: "#94a3b8", textColor: "#0a1428" },
        { id: "doc",  label: "Filing 184729",      x: 130, y: 300, w: 80.6,  fill: "#fff",    stroke: "#94a3b8", textColor: "#0a1428" },
        { id: "addr", label: "Munich, DE",         x: 200, y: 70,  w: 70,    fill: "#fff",    stroke: "#94a3b8", textColor: "#0a1428" },
      ],
      kgEdges: [
        { from: 0, to: 1, label: "owns 92%" },
        { from: 1, to: 2, label: "parent"   },
        { from: 2, to: 3, label: "listed"   },
        { from: 0, to: 4, label: "officer"  },
        { from: 4, to: 2, label: "linked"   },
        { from: 0, to: 5, label: "filed in" },
        { from: 0, to: 6, label: "filing"   },
        { from: 0, to: 7, label: "registered" },
      ],
    };
  },
  async mounted() {
    this._kgInterval = setInterval(() => {
      this.kgPulse = (this.kgPulse + 1) % this.kgEdges.length;
    }, 900);

    const defaults = {
      hero_badge: this.hero_badge, hero_badge_sub: this.hero_badge_sub,
      hero_title_prefix: this.hero_title_prefix, hero_title_accent: this.hero_title_accent,
      hero_title_suffix: this.hero_title_suffix, hero_subtitle: this.hero_subtitle,
      hero_search_placeholder: this.hero_search_placeholder,
      hero_samples: this.samples, hero_trust_items: this.trustItems,
      hero_card_tag: this.hero_card_tag, hero_card_query: this.hero_card_query,
      hero_card_answer: this.hero_card_answer, hero_card_verdict: this.hero_card_verdict,
      hero_card_steps: this.hero_card_steps,
      hero_card_footer_left: this.hero_card_footer_left, hero_card_footer_right: this.hero_card_footer_right,
      evidence_eyebrow: this.evidence_eyebrow, evidence_title: this.evidence_title,
      evidence_sub: this.evidence_sub, evidence_regs_label: this.evidence_regs_label,
      evidence_regs: this.regs, evidence_stakes_label: this.evidence_stakes_label,
      evidence_stakes_num: this.evidence_stakes_num, evidence_stakes_sub: this.evidence_stakes_sub,
      evidence_cta: this.evidence_cta,
      teams_eyebrow: this.teams_eyebrow, teams_title: this.teams_title, teams_sub: this.teams_sub,
      teams: this.teams,
      proof_eyebrow: this.proof_eyebrow, proof_title: this.proof_title, cases: this.cases,
      how_eyebrow: this.how_eyebrow, how_title: this.how_title, how_sub: this.how_sub,
      layers: this.layers, kg_caption: this.kg_caption,
      kg_nodes: this.kgNodes.map(n => ({ ...n, text_color: n.textColor })),
      kg_edges: this.kgEdges.map(e => ({ from_node: e.from, to_node: e.to, label: e.label })),
      sovereign_eyebrow: this.sovereign_eyebrow, sovereign_title: this.sovereign_title,
      sovereign_desc: this.sovereign_desc, sovereign_tags: this.sovereignTags,
      sovereign_blocks: this.sovereignBlocks,
      deploy_eyebrow: this.deploy_eyebrow, deploy_title: this.deploy_title, deploy_sub: this.deploy_sub,
      deployments: this.deployments,
      cta_badge: this.cta_badge, cta_title: this.cta_title, cta_sub: this.cta_sub,
      cta_btn_primary: this.cta_btn_primary, cta_btn_ghost: this.cta_btn_ghost, cta_quote: this.cta_quote,
    };

    const applyHome = (cms) => {
      const textFields = [
        'hero_badge','hero_badge_sub','hero_title_prefix','hero_title_accent','hero_title_suffix',
        'hero_subtitle','hero_search_placeholder','hero_card_tag','hero_card_query','hero_card_answer',
        'hero_card_verdict','hero_card_footer_left','hero_card_footer_right',
        'evidence_eyebrow','evidence_title','evidence_sub','evidence_regs_label',
        'evidence_stakes_label','evidence_stakes_num','evidence_stakes_sub','evidence_cta',
        'teams_eyebrow','teams_title','teams_sub','proof_eyebrow','proof_title',
        'how_eyebrow','how_title','how_sub','kg_caption',
        'sovereign_eyebrow','sovereign_title','sovereign_desc',
        'deploy_eyebrow','deploy_title','deploy_sub',
        'cta_badge','cta_title','cta_sub','cta_btn_primary','cta_btn_ghost','cta_quote',
      ];
      textFields.forEach(f => { if (cms[f]) this[f] = cms[f]; });
      if (cms.hero_samples?.length)    this.samples        = cms.hero_samples;
      if (cms.hero_trust_items?.length) this.trustItems     = cms.hero_trust_items;
      if (cms.hero_card_steps?.length)  this.hero_card_steps = cms.hero_card_steps;
      if (cms.evidence_regs?.length)   this.regs           = cms.evidence_regs;
      if (cms.teams?.length)           this.teams          = cms.teams;
      if (cms.cases?.length)           this.cases          = cms.cases;
      if (cms.layers?.length)           this.layers         = cms.layers;
      // Knowledge graph — map Wagtail field names to frontend format
      if (cms.kg_nodes?.length) {
        this.kgNodes = cms.kg_nodes.map(n => ({
          id: n.id, label: n.label, x: n.x, y: n.y, w: n.w,
          fill: n.fill, stroke: n.stroke, textColor: n.text_color,
        }));
      }
      if (cms.kg_edges?.length) {
        this.kgEdges = cms.kg_edges.map(e => ({
          from: e.from_node, to: e.to_node, label: e.label,
        }));
      }
      if (cms.sovereign_tags?.length)  this.sovereignTags  = cms.sovereign_tags;
      if (cms.sovereign_blocks?.length)this.sovereignBlocks = cms.sovereign_blocks.map(b => ({ ...b, desc: b.body || b.desc }));
      if (cms.deployments?.length)     this.deployments    = cms.deployments;
    };
    const cms = await cmsHome(defaults, applyHome.bind(this));
    applyHome(cms);
  },
  beforeDestroy() {
    if (this._kgInterval) clearInterval(this._kgInterval);
  },
  methods: {
    doSearch() {
      this.$router.push(this.query.trim() ? "/search?q=" + encodeURIComponent(this.query.trim()) : "/search");
    },
  },
};
</script>

<style scoped>
/* ── Base ── */
.home-page { background: #fff; color: #0a1428; }
.container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
.eyebrow { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.1em; color: #575ECF; margin-bottom: 12px; }
.section-title { font-size: clamp(1.6rem, 3vw, 2.25rem); font-weight: 600; letter-spacing: -0.02em; color: #0a1428; line-height: 1.2; margin-bottom: 16px; }
.section-sub { font-size: 1.05rem; color: #64748b; line-height: 1.7; max-width: 600px; }
.section-sub strong { color: #0a1428; font-weight: 500; }
.text-primary { color: #575ECF; }
.btn-primary { display: inline-flex; align-items: center; gap: 6px; background: #575ECF; color: #fff; border-radius: 10px; padding: 10px 20px; font-size: 13px; font-weight: 500; text-decoration: none; transition: background 0.15s; cursor: pointer; border: none; }
.btn-primary:hover { background: #4750b8; text-decoration: none; color: #fff; }

/* ── Hero ── */
.hero { border-bottom: 1px solid #e2e8f0; padding: 40px 0 96px; overflow: hidden; }
.hero__eyebrow { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; margin-bottom: 48px; }
.hero__badge { display: inline-flex; align-items: center; gap: 6px; background: rgba(87,94,207,0.08); color: #575ECF; border-radius: 100px; padding: 5px 12px; font-size: 12px; font-weight: 500; }
.hero__badge-sub { font-size: 12px; color: #94a3b8; }
.hero__grid { display: grid; grid-template-columns: 1.15fr 1fr; gap: 48px; align-items: start; }
.hero__title { font-size: clamp(2rem, 4vw, 3.5rem); font-weight: 600; letter-spacing: -0.02em; color: #0a1428; line-height: 1.05; margin-bottom: 28px; }
.hero__accent { color: #575ECF; }
.hero__sub { font-size: 1.1rem; color: #64748b; line-height: 1.7; max-width: 520px; margin-bottom: 36px; }
.hero__sub strong { color: #0a1428; font-weight: 500; }
.hero__search-wrap { display: flex; align-items: center; gap: 8px; border: 1px solid #e2e8f0; border-radius: 16px; background: #fff; box-shadow: 0 2px 12px rgba(0,0,0,0.05); padding: 6px 6px 6px 14px; transition: box-shadow 0.2s, border-color 0.2s; }
.hero__search-wrap:focus-within { border-color: rgba(87,94,207,0.4); box-shadow: 0 0 0 3px rgba(87,94,207,0.1); }
.hero__search-icon { color: #94a3b8; font-size: 15px; flex-shrink: 0; }
.hero__search-input { flex: 1; border: none; outline: none; font-size: 15px; color: #0a1428; background: transparent; padding: 8px 4px; }
.hero__search-input::placeholder { color: #94a3b8; }
.hero__search-btn { display: inline-flex; align-items: center; gap: 8px; background: #066cd9; color: #fff; border: none; border-radius: 50px; padding: 10px 22px; font-size: 15px; font-weight: 400; font-family: 'Poppins', sans-serif; letter-spacing: 0.5px; cursor: pointer; white-space: nowrap; transition: background 0.15s, box-shadow 0.15s; text-transform: capitalize; box-shadow: 0 4px 6px rgba(50,50,93,0.1), 0 1px 3px rgba(0,0,0,0.08); }
.hero__search-btn:hover { background: #0558b0; box-shadow: 0 7px 14px rgba(50,50,93,0.1), 0 3px 6px rgba(0,0,0,0.08); }
.hero__samples { margin-top: 14px; }
.hero__samples-label { font-size: 12px; color: #94a3b8; margin-bottom: 8px; }
.hero__samples-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.hero__sample-chip { display: inline-flex; align-items: center; gap: 4px; border: 1px solid #e2e8f0; background: #fff; border-radius: 100px; padding: 5px 12px; font-size: 12px; color: #64748b; cursor: pointer; transition: border-color 0.15s, color 0.15s, background 0.15s; }
.hero__sample-chip:hover { border-color: rgba(87,94,207,0.4); background: rgba(87,94,207,0.04); color: #0a1428; }
.hero__chip-arrow { font-size: 11px; opacity: 0; margin-left: -4px; transition: opacity 0.15s, margin 0.15s; }
.hero__sample-chip:hover .hero__chip-arrow { opacity: 1; margin-left: 0; }
.hero__trust { display: flex; flex-wrap: wrap; gap: 20px; margin-top: 32px; }
.hero__trust-item { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #64748b; }
.hero__trust-item .fa { color: #575ECF; font-size: 14px; }

/* Hero card */
.hero__card { background: #0a1428; color: #fff; border-radius: 20px; overflow: hidden; box-shadow: 0 20px 60px rgba(87,94,207,0.2); position: relative; }
.hero__card-grid-bg { position: absolute; inset: 0; opacity: 0.18; background-image: linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px); background-size: 36px 36px; pointer-events: none; }
.hero__card-inner { position: relative; padding: 24px 28px; }
.hero__card-header { display: flex; align-items: center; justify-content: space-between; font-size: 11px; margin-bottom: 20px; }
.hero__card-tag { display: inline-flex; align-items: center; gap: 6px; background: rgba(255,255,255,0.1); border-radius: 100px; padding: 4px 10px; backdrop-filter: blur(4px); }
.hero__card-live { display: inline-flex; align-items: center; gap: 5px; background: rgba(74,222,128,0.15); color: #4ade80; border-radius: 100px; padding: 4px 10px; font-weight: 500; }
.hero__card-dot { width: 6px; height: 6px; border-radius: 50%; background: #4ade80; animation: pulse 1.5s infinite; }
@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
.hero__card-query { font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: rgba(255,255,255,0.45); margin-bottom: 8px; }
.hero__card-answer { font-size: 15px; line-height: 1.6; color: rgba(255,255,255,0.9); margin-bottom: 20px; }
.hero__card-verdict { color: #4ade80; font-weight: 600; }
.hero__card-steps-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.1em; color: rgba(255,255,255,0.4); margin-bottom: 8px; }
.hero__card-steps { display: flex; flex-direction: column; gap: 8px; }
.hero__card-step { display: flex; align-items: flex-start; gap: 8px; font-size: 13px; color: rgba(255,255,255,0.8); }
.hero__card-step .fa { color: #4ade80; margin-top: 2px; flex-shrink: 0; }
.hero__card-footer { display: flex; align-items: center; justify-content: space-between; border-top: 1px solid rgba(255,255,255,0.1); margin-top: 20px; padding-top: 14px; font-size: 11px; color: rgba(255,255,255,0.5); }

/* ── Evidence Gap ── */
.evidence { border-bottom: 1px solid #e2e8f0; background: #f8fafc; padding: 64px 0 72px; }
.evidence__header { max-width: 720px; margin-bottom: 32px; }
.evidence__card { border: 1px solid #e2e8f0; border-radius: 16px; background: #fff; padding: 20px 24px; margin-bottom: 28px; }
.evidence__card-inner { display: flex; flex-direction: column; gap: 20px; }
@media (min-width: 1024px) { .evidence__card-inner { flex-direction: row; align-items: center; justify-content: space-between; } }
.evidence__regs-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: #94a3b8; font-weight: 600; margin-bottom: 12px; }
.evidence__regs-tags { display: flex; flex-wrap: wrap; gap: 8px; }
.evidence__reg-tag { border: 1px solid #e2e8f0; background: #f8fafc; border-radius: 6px; padding: 4px 10px; font-size: 12px; font-weight: 600; color: #0a1428; }
.evidence__stakes { padding-left: 0; border-top: 1px solid #e2e8f0; padding-top: 20px; }
@media (min-width: 1024px) { .evidence__stakes { border-top: none; border-left: 1px solid #e2e8f0; padding-left: 32px; padding-top: 0; min-width: 260px; } }
.evidence__stakes-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: #94a3b8; font-weight: 600; margin-bottom: 8px; }
.evidence__stakes-num { font-size: 1.5rem; font-weight: 600; color: #0a1428; margin-bottom: 4px; }
.evidence__stakes-sub { font-size: 12px; color: #64748b; }

/* ── Teams ── */
.teams { padding: 80px 0; background: #fff; }
.teams__header { max-width: 600px; margin-bottom: 40px; }
.teams__grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 16px; }
.team-card { border: 1px solid #e2e8f0; border-radius: 12px; background: #f8fafc; padding: 24px; transition: border-color 0.2s, box-shadow 0.2s; }
.team-card:hover { border-color: rgba(87,94,207,0.35); box-shadow: 0 2px 16px rgba(87,94,207,0.07); }
.team-card__icon { width: 40px; height: 40px; border-radius: 10px; background: rgba(87,94,207,0.1); color: #575ECF; display: flex; align-items: center; justify-content: center; font-size: 16px; margin-bottom: 16px; }
.team-card__name { font-size: 14px; font-weight: 600; color: #0a1428; margin-bottom: 6px; }
.team-card__desc { font-size: 13px; color: #64748b; line-height: 1.6; margin-bottom: 16px; }
.team-card__tags { display: flex; flex-wrap: wrap; gap: 6px; }
.team-tag { background: rgba(87,94,207,0.06); color: #575ECF; border-radius: 6px; padding: 3px 8px; font-size: 11px; font-weight: 500; }

/* ── Proof ── */
.proof { border-top: 1px solid #e2e8f0; border-bottom: 1px solid #e2e8f0; background: #f8fafc; padding: 80px 0; }
.proof__header { max-width: 540px; margin-bottom: 48px; }
.proof__grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 20px; }
.proof-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 24px; display: flex; flex-direction: column; cursor: pointer; transition: border-color 0.2s, box-shadow 0.2s; }
.proof-card:hover { border-color: rgba(87,94,207,0.35); box-shadow: 0 4px 20px rgba(87,94,207,0.08); }
.proof-card__tag { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; color: #575ECF; margin-bottom: 8px; }
.proof-card__title { font-size: 1.05rem; font-weight: 600; color: #0a1428; margin-bottom: 20px; transition: color 0.15s; }
.proof-card:hover .proof-card__title { color: #575ECF; }
.proof-card__body { flex: 1; display: flex; flex-direction: column; gap: 16px; margin-bottom: 20px; }
.proof-card__block-label { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 4px; }
.proof-card__block-label.before { color: #94a3b8; }
.proof-card__block-label.after { color: #575ECF; }
.proof-card__block-text { font-size: 13px; color: #475569; line-height: 1.65; }
.proof-card__footer { border-top: 1px solid #e2e8f0; padding-top: 16px; display: flex; align-items: center; justify-content: space-between; }
.proof-card__metric { font-size: 14px; font-weight: 600; color: #0a1428; }
.proof-card__open { font-size: 12px; color: #575ECF; display: inline-flex; align-items: center; gap: 4px; opacity: 0; transition: opacity 0.15s; }
.proof-card:hover .proof-card__open { opacity: 1; }

/* ── How it works ── */
.how { padding: 80px 0; background: #fff; }
.how__grid { display: grid; grid-template-columns: 1fr 1.15fr; gap: 48px; align-items: start; }
.how__left .section-title { margin-top: 12px; }
.how__left .section-sub { margin-bottom: 32px; }
.how__layers { display: flex; flex-direction: column; gap: 12px; }
.layer-card { border: 1px solid #e2e8f0; border-radius: 12px; background: #f8fafc; padding: 20px 22px; }
.layer-card__header { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
.layer-card__icon { width: 36px; height: 36px; border-radius: 9px; background: rgba(87,94,207,0.1); color: #575ECF; display: flex; align-items: center; justify-content: center; font-size: 15px; flex-shrink: 0; }
.layer-card__title { font-size: 14px; font-weight: 600; color: #0a1428; flex: 1; }
.layer-card__tag { font-size: 11px; color: #94a3b8; font-family: monospace; }
.layer-card__desc { font-size: 13px; color: #64748b; line-height: 1.7; margin-bottom: 12px; }
.layer-card__proofs { display: flex; flex-wrap: wrap; gap: 6px; }
.layer-proof { display: inline-flex; align-items: center; gap: 5px; background: rgba(87,94,207,0.06); color: #575ECF; border-radius: 6px; padding: 3px 9px; font-size: 11px; font-weight: 500; }
.layer-proof .fa { font-size: 10px; }
.how__right { position: sticky; top: 88px; }
.kg-caption { margin-top: 10px; font-size: 12px; color: #94a3b8; }

/* Knowledge Graph */
.kg-panel { border: 1px solid #e2e8f0; border-radius: 16px; background: #fff; overflow: hidden; box-shadow: 0 2px 12px rgba(0,0,0,0.04); position: relative; }
.kg-panel__badge { position: absolute; top: 12px; left: 12px; z-index: 10; display: inline-flex; align-items: center; gap: 6px; background: rgba(87,94,207,0.1); color: #575ECF; border-radius: 100px; padding: 4px 10px; font-size: 11px; font-weight: 500; }
.kg-panel__dot { width: 6px; height: 6px; border-radius: 50%; background: #575ECF; animation: pulse 1.5s infinite; }
.kg-panel__info { position: absolute; top: 12px; right: 12px; z-index: 10; font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em; color: #94a3b8; }
.kg-svg { width: 100%; height: auto; display: block; }
.kg-panel__legend { border-top: 1px solid #e2e8f0; padding: 10px 16px; display: flex; flex-wrap: wrap; align-items: center; gap: 16px; }
.kg-legend-item { display: inline-flex; align-items: center; gap: 6px; font-size: 11px; color: #64748b; }
.kg-legend-item::before { content: ''; width: 8px; height: 8px; border-radius: 50%; display: inline-block; flex-shrink: 0; }
.kg-legend-item--primary::before { background: #575ECF; }
.kg-legend-item--danger::before { background: #dc2626; }
.kg-legend-item--person::before { background: #fff; border: 1.5px solid #575ECF; }
.kg-legend-item--neutral::before { background: #fff; border: 1.5px solid #94a3b8; }

/* ── Sovereign ── */
.sovereign { position: relative; overflow: hidden; background: #0a1428; color: #fff; padding: 80px 0; }
.sovereign__grid-bg { position: absolute; inset: 0; opacity: 0.12; background-image: linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px); background-size: 40px 40px; pointer-events: none; }
.sovereign__inner { position: relative; display: grid; grid-template-columns: 1.1fr 1fr; gap: 48px; align-items: center; }
.sovereign__eyebrow { display: inline-flex; align-items: center; gap: 6px; background: rgba(255,255,255,0.05); border-radius: 100px; padding: 3px 10px; font-size: 11px; color: rgba(255,255,255,0.7); margin-bottom: 16px; }
.sovereign__title { font-size: clamp(1.6rem, 3vw, 2.25rem); font-weight: 600; letter-spacing: -0.02em; color: #fff; line-height: 1.2; margin-bottom: 16px; }
.sovereign__desc { font-size: 1rem; color: rgba(255,255,255,0.7); line-height: 1.7; max-width: 520px; margin-bottom: 24px; }
.sovereign__tags { display: flex; flex-wrap: wrap; gap: 8px; }
.sovereign__tag { background: rgba(255,255,255,0.1); border-radius: 6px; padding: 5px 12px; font-size: 12px; color: rgba(255,255,255,0.85); }
.sovereign__cards { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.sovereign-card { border: 1px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.04); border-radius: 12px; padding: 20px; backdrop-filter: blur(4px); }
.sovereign-card__icon { font-size: 18px; color: #4ade80; margin-bottom: 12px; display: block; }
.sovereign-card__title { font-size: 14px; font-weight: 600; color: #fff; margin-bottom: 6px; }
.sovereign-card__desc { font-size: 13px; color: rgba(255,255,255,0.65); line-height: 1.6; }

/* ── Deploy ── */
.deploy { padding: 80px 0; background: #fff; }
.deploy__grid { display: grid; grid-template-columns: 1fr 2fr; gap: 48px; align-items: start; }
.deploy__cards { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.deploy-card { border: 1px solid #e2e8f0; border-radius: 12px; background: #f8fafc; padding: 20px; transition: border-color 0.15s; }
.deploy-card:hover { border-color: rgba(87,94,207,0.35); }
.deploy-card__icon { font-size: 18px; color: #575ECF; margin-bottom: 12px; display: block; }
.deploy-card__name { font-size: 14px; font-weight: 600; color: #0a1428; margin-bottom: 6px; }
.deploy-card__desc { font-size: 13px; color: #64748b; line-height: 1.6; }

/* ── Final CTA ── */
.final-cta-wrap { padding: 80px 0; background: #fff; }
.final-cta { position: relative; border-radius: 20px; background: #0a1428; color: #fff; padding: 56px 64px; text-align: center; overflow: hidden; }
.final-cta__grid-bg { position: absolute; inset: 0; opacity: 0.12; background-image: linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px); background-size: 36px 36px; pointer-events: none; }
.final-cta__inner { position: relative; }
.final-cta__badge { display: inline-flex; align-items: center; gap: 6px; background: rgba(255,255,255,0.1); border-radius: 100px; padding: 4px 12px; font-size: 12px; color: rgba(255,255,255,0.85); margin-bottom: 20px; }
.final-cta__title { font-size: clamp(1.75rem, 3vw, 2.5rem); font-weight: 600; color: #fff; letter-spacing: -0.02em; margin-bottom: 16px; }
.final-cta__sub { font-size: 1.05rem; color: rgba(255,255,255,0.7); max-width: 520px; margin: 0 auto 32px; line-height: 1.7; }
.final-cta__actions { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; margin-bottom: 24px; }
.final-cta-btn { display: inline-flex; align-items: center; gap: 6px; border-radius: 12px; padding: 12px 24px; font-size: 14px; font-weight: 600; text-decoration: none; transition: all 0.15s; cursor: pointer; }
.final-cta-btn--white { background: #fff; color: #0a1428; }
.final-cta-btn--white:hover { background: rgba(255,255,255,0.9); text-decoration: none; color: #0a1428; }
.final-cta-btn--ghost { background: rgba(255,255,255,0.05); color: #fff; border: 1px solid rgba(255,255,255,0.2); }
.final-cta-btn--ghost:hover { background: rgba(255,255,255,0.1); text-decoration: none; color: #fff; }
.final-cta__quote { font-size: 12px; color: rgba(255,255,255,0.5); display: inline-flex; align-items: center; gap: 6px; }

/* ── Responsive ── */
@media (max-width: 1024px) {
  .hero__grid { grid-template-columns: 1fr; gap: 32px; }
  .proof__grid { grid-template-columns: repeat(2, 1fr); }
  .how__grid { grid-template-columns: 1fr; }
  .how__right { position: static; }
  .sovereign__inner { grid-template-columns: 1fr; }
  .deploy__grid { grid-template-columns: 1fr; }
  .teams__grid { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 768px) {
  /* Section spacing */
  .hero        { padding: 28px 0 48px; }
  .evidence    { padding: 48px 0 48px; }
  .teams       { padding: 56px 0; }
  .proof       { padding: 56px 0; }
  .how         { padding: 56px 0; }
  .sovereign   { padding: 56px 0; }
  .deploy      { padding: 56px 0; }
  .final-cta-wrap { padding: 48px 0; }
  .final-cta   { padding: 36px 24px; }

  /* Hero text */
  .hero__eyebrow { margin-bottom: 24px; }
  .hero__sub { font-size: 1rem; margin-bottom: 28px; }

  /* Hero search — wrap button onto its own row */
  .hero__search-wrap { flex-wrap: wrap; padding: 8px 10px; border-radius: 14px; gap: 6px; }
  .hero__search-input { width: 100%; order: 2; padding: 6px 4px; }
  .hero__search-icon { order: 1; }
  .hero__search-btn { order: 3; width: 100%; justify-content: center; border-radius: 10px; padding: 11px 16px; }

  /* Trust strip */
  .hero__trust { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-top: 24px; }
  .hero__trust-item { font-size: 12px; }

  /* Grids */
  .proof__grid { grid-template-columns: 1fr; }
  .sovereign__cards { grid-template-columns: 1fr 1fr; gap: 12px; }
  .deploy__cards { grid-template-columns: 1fr; }

  /* Knowledge graph */
  .how__right { overflow: hidden; }
  .kg-svg { width: 100%; }

  /* Section text */
  .section-sub { font-size: 0.95rem; }
  .teams__header, .proof__header { margin-bottom: 28px; }
}

@media (max-width: 560px) {
  /* Hero */
  .hero { padding: 20px 0 36px; }
  .hero__badge-sub { display: none; }
  .hero__title { margin-bottom: 20px; }
  .hero__samples-chips { flex-wrap: nowrap; overflow-x: auto; -webkit-overflow-scrolling: touch; padding-bottom: 4px; }
  .hero__sample-chip { flex-shrink: 0; }

  /* Hero card — compact */
  .hero__card-inner { padding: 16px 18px; }
  .hero__card-steps { display: none; }
  .hero__card-footer { display: none; }

  /* Teams */
  .teams__grid { grid-template-columns: 1fr; }
  .team-card { display: flex; gap: 16px; align-items: flex-start; padding: 16px 18px; }
  .team-card__icon { margin-bottom: 0; flex-shrink: 0; }
  .team-card__tags { margin-top: 8px; }

  /* Sovereign */
  .sovereign__cards { grid-template-columns: 1fr; }
  .sovereign__tags { gap: 6px; }
  .sovereign__tag { font-size: 11px; padding: 4px 10px; }

  /* Proof */
  .proof-card { padding: 20px; }

  /* Final CTA */
  .final-cta__actions { flex-direction: column; align-items: stretch; }
  .final-cta-btn { justify-content: center; }

  /* Deploy */
  .deploy__cards { grid-template-columns: 1fr; }

  /* Evidence */
  .evidence__stakes-num { font-size: 1.25rem; }
}
</style>
